import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';

const TestDataViewer = () => {
  const [students, setStudents] = useState([]);
  const [interventionGroups, setInterventionGroups] = useState([]);
  const [progressRecords, setProgressRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('students');
  const [filterPhase, setFilterPhase] = useState('all');
  const [filterEHCP, setFilterEHCP] = useState('all');

  useEffect(() => {
    loadTestData();
  }, []);

  const loadTestData = async () => {
    try {
      setLoading(true);
      
      // Load students
      const studentsQuery = query(collection(db, 'students'), orderBy('lastName'), limit(50));
      const studentsSnapshot = await getDocs(studentsQuery);
      const studentsData = studentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setStudents(studentsData);

      // Load intervention groups
      const groupsQuery = query(collection(db, 'interventionGroups'), orderBy('name'));
      const groupsSnapshot = await getDocs(groupsQuery);
      const groupsData = groupsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setInterventionGroups(groupsData);

      // Load progress records
      const progressQuery = query(collection(db, 'progressRecords'), orderBy('date', 'desc'), limit(50));
      const progressSnapshot = await getDocs(progressQuery);
      const progressData = progressSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProgressRecords(progressData);

    } catch (error) {
      console.error('Error loading test data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredStudents = students.filter(student => {
    if (filterPhase !== 'all' && student.phase !== filterPhase) return false;
    if (filterEHCP !== 'all') {
      if (filterEHCP === 'with' && !student.hasEHCP) return false;
      if (filterEHCP === 'without' && student.hasEHCP) return false;
    }
    return true;
  });

  const getEHCPBadge = (hasEHCP) => {
    return hasEHCP ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        EHCP
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        No EHCP
      </span>
    );
  };

  const getPhaseBadge = (phase) => {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        phase === 'primary' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
      }`}>
        {phase.charAt(0).toUpperCase() + phase.slice(1)}
      </span>
    );
  };

  const getAcademicLevelBadge = (level) => {
    const colors = {
      'working below': 'bg-yellow-100 text-yellow-800',
      'working at': 'bg-green-100 text-green-800',
      'working above': 'bg-blue-100 text-blue-800'
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[level]}`}>
        {level.replace('working ', '')}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Test Data Viewer</h1>
        <p className="text-gray-600">
          View and explore the populated test data including students, intervention groups, and progress records.
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900">Total Students</h3>
          <p className="text-3xl font-bold text-blue-600">{students.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900">Intervention Groups</h3>
          <p className="text-3xl font-bold text-green-600">{interventionGroups.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900">Progress Records</h3>
          <p className="text-3xl font-bold text-purple-600">{progressRecords.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900">Students with EHCP</h3>
          <p className="text-3xl font-bold text-red-600">
            {students.filter(s => s.hasEHCP).length}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Phase</label>
            <select
              value={filterPhase}
              onChange={(e) => setFilterPhase(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="all">All Phases</option>
              <option value="primary">Primary</option>
              <option value="secondary">Secondary</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">EHCP Status</label>
            <select
              value={filterEHCP}
              onChange={(e) => setFilterEHCP(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="all">All Students</option>
              <option value="with">With EHCP</option>
              <option value="without">Without EHCP</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('students')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'students'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Students ({filteredStudents.length})
            </button>
            <button
              onClick={() => setActiveTab('groups')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'groups'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Intervention Groups ({interventionGroups.length})
            </button>
            <button
              onClick={() => setActiveTab('progress')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'progress'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Progress Records ({progressRecords.length})
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Students Tab */}
          {activeTab === 'students' && (
            <div>
              {/* Year Group Organization */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Students by Year Group</h3>
                <div className="space-y-4">
                  {Object.entries(
                    filteredStudents.reduce((acc, student) => {
                      if (!acc[student.yearGroup]) {
                        acc[student.yearGroup] = [];
                      }
                      acc[student.yearGroup].push(student);
                      return acc;
                    }, {})
                  )
                    .sort(([a], [b]) => {
                      const yearOrder = {
                        'Reception': 0, 'Year 1': 1, 'Year 2': 2, 'Year 3': 3,
                        'Year 4': 4, 'Year 5': 5, 'Year 6': 6, 'Year 7': 7,
                        'Year 8': 8, 'Year 9': 9, 'Year 10': 10, 'Year 11': 11
                      };
                      return yearOrder[a] - yearOrder[b];
                    })
                    .map(([yearGroup, students]) => (
                      <div key={yearGroup} className="border border-gray-200 rounded-lg">
                        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-gray-900">
                              {yearGroup} ({students.length} students)
                            </h4>
                            <span className="text-sm text-gray-500">
                              {students.filter(s => s.hasEHCP).length} with EHCP
                            </span>
                          </div>
                        </div>
                        <div className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {students
                              .sort((a, b) => a.lastName.localeCompare(b.lastName))
                              .map((student) => (
                                <div key={student.id} className="bg-white border border-gray-200 rounded-lg p-3">
                                  <div className="flex items-start justify-between mb-2">
                                    <h5 className="font-medium text-gray-900 text-sm">
                                      {student.firstName} {student.lastName}
                                    </h5>
                                    {getEHCPBadge(student.hasEHCP)}
                                  </div>
                                  <div className="text-xs text-gray-600 space-y-1">
                                    <div>DOB: {student.dateOfBirth}</div>
                                    <div>Level: {getAcademicLevelBadge(student.academicLevel)}</div>
                                    <div>Groups: {student.interventionGroups.length}</div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* Detailed Table View */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Detailed Student List</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Year Group</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phase</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">EHCP</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Level</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Groups</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredStudents.map((student) => (
                        <tr key={student.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {student.firstName} {student.lastName}
                            </div>
                            <div className="text-sm text-gray-500">{student.dateOfBirth}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {student.yearGroup}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getPhaseBadge(student.phase)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getEHCPBadge(student.hasEHCP)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getAcademicLevelBadge(student.academicLevel)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {student.interventionGroups.length} group(s)
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Intervention Groups Tab */}
          {activeTab === 'groups' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {interventionGroups.map((group) => (
                <div key={group.id} className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{group.name}</h3>
                  <p className="text-gray-600 text-sm mb-4">{group.description}</p>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Phase:</span>
                      <span className="text-sm font-medium">{getPhaseBadge(group.phase)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Focus Area:</span>
                      <span className="text-sm font-medium">{group.focusArea}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Students:</span>
                      <span className="text-sm font-medium">{group.currentStudents}/{group.maxStudents}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Schedule:</span>
                      <span className="text-sm font-medium">{group.schedule.day} {group.schedule.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Progress Records Tab */}
          {activeTab === 'progress' && (
            <div className="space-y-4">
              {progressRecords.map((record) => (
                <div key={record.id} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="text-sm font-medium text-gray-900">{record.target}</h4>
                    <span className="text-xs text-gray-500">
                      {record.date.toDate ? record.date.toDate().toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Current Level:</span>
                      <span className="ml-2 font-medium">{record.currentLevel}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Target Level:</span>
                      <span className="ml-2 font-medium">{record.targetLevel}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Notes:</span>
                      <span className="ml-2 font-medium">{record.notes}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Next Steps:</span>
                      <span className="ml-2 font-medium">{record.nextSteps}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestDataViewer;
